import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Switch,
  FormControlLabel, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import { adminApi } from '../../api';
import { useAuth } from '../../AuthContext';
import StudyPerformanceMonitoring from './StudyPerformanceMonitoring';

const statusColors = {
  healthy: { color: '#66bb6a', bgcolor: '#102016', border: '#1f5f30' },
  warning: { color: '#ffb74d', bgcolor: '#2a1f0b', border: '#6b4a15' },
  down: { color: '#ef5350', bgcolor: '#2a1111', border: '#6b1f1f' },
};

const StatusChip = ({ status }) => {
  const sx = statusColors[status] || statusColors.warning;
  return <Chip size="small" label={(status || 'unknown').toUpperCase()} sx={{ ...sx, border: `1px solid ${sx.border}`, fontFamily: 'monospace', fontSize: 11 }} />;
};

const SummaryCard = ({ label, value, color, icon }) => (
  <Paper sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', p: 2.5, borderRadius: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box>
        <Typography sx={{ color: '#555', fontSize: 11, letterSpacing: 1, fontFamily: 'monospace' }}>{label.toUpperCase()}</Typography>
        <Typography sx={{ color, fontSize: 30, fontWeight: 700, fontFamily: 'monospace', mt: 0.5 }}>{value ?? '—'}</Typography>
      </Box>
      <Box sx={{ color: '#222', fontSize: 38 }}>{icon}</Box>
    </Box>
  </Paper>
);

const formatDetails = (details = {}) => {
  const entries = Object.entries(details || {});
  if (!entries.length) return '—';
  return entries.map(([k, v]) => `${k}: ${String(v)}`).join(' · ');
};

const BUILT_IN_MONITORING_CRITICAL_SLOWEST_MARKER = 'KAIROS_MONITORING_SLOWEST_CRITICAL_ONLY';

const BuiltInMonitoring = () => {
  const { user } = useAuth();
  const canBrowseStudyPerformance = (user?.roles || []).some(role => ['admin', 'superadmin'].includes(String(role).toLowerCase()));
  const canSearchStudyPerformanceUsers = (user?.roles || []).some(role => String(role).toLowerCase() === 'superadmin');
  const [section, setSection] = useState('health');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [error, setError] = useState(null);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const result = await adminApi.getMonitoringHealth();
      setData(result);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || e.message || 'Unable to load monitoring health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!autoRefresh || section !== 'health') return undefined;
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, [autoRefresh, section]);

  const slowestCritical = useMemo(() => {
    const criticalChecks = (data?.checks || []).filter((check) => check.critical);
    if (!criticalChecks.length) return null;
    return criticalChecks.reduce((max, c) => (c.latency_ms || 0) > (max?.latency_ms || -1) ? c : max, null);
  }, [data]);

  const overall = data?.overall_status || 'warning';
  const overallStyle = statusColors[overall] || statusColors.warning;

  return (
    <Box data-monitoring-marker={BUILT_IN_MONITORING_CRITICAL_SLOWEST_MARKER}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1 }}>
            Built-in Monitoring
          </Typography>
          <Typography sx={{ color: '#666', fontSize: 12, mt: 0.5 }}>
            First-pass health, latency, and service speed checks for the Kairos viewer stack.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FormControlLabel
            control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
            label={<Typography sx={{ color: '#777', fontSize: 12 }}>Auto-refresh 30s</Typography>}
          />
          <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={() => load(true)} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      <Box role="tablist" aria-label="Built-in monitoring pages" sx={{ display: 'flex', gap: 0.75, mb: 2.5, p: 0.6, width: 'fit-content',
        bgcolor: 'rgba(5,10,14,0.92)', border: '1px solid rgba(110,239,255,0.12)', borderRadius: 2 }}>
        <Button role="tab" aria-selected={section === 'health'} size="small" onClick={() => setSection('health')} sx={{
          px: 1.8, color: section === 'health' ? '#6eefff' : '#607985', bgcolor: section === 'health' ? 'rgba(110,239,255,0.08)' : 'transparent',
          border: section === 'health' ? '1px solid rgba(110,239,255,0.22)' : '1px solid transparent', fontFamily: 'monospace', fontSize: 10,
        }}>SYSTEM HEALTH</Button>
        {canBrowseStudyPerformance && <Button role="tab" aria-selected={section === 'performance'} size="small" onClick={() => setSection('performance')} sx={{
          px: 1.8, color: section === 'performance' ? '#6eefff' : '#607985', bgcolor: section === 'performance' ? 'rgba(110,239,255,0.08)' : 'transparent',
          border: section === 'performance' ? '1px solid rgba(110,239,255,0.22)' : '1px solid transparent', fontFamily: 'monospace', fontSize: 10,
        }}>STUDY PERFORMANCE</Button>}
      </Box>

      {section === 'performance' ? <StudyPerformanceMonitoring allowUserFilter={canSearchStudyPerformanceUsers} /> : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
      ) : <>
      {error && <Alert severity="error" sx={{ mb: 2, bgcolor: '#2a1111', color: '#ffcdd2', border: '1px solid #6b1f1f' }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ bgcolor: overallStyle.bgcolor, border: `1px solid ${overallStyle.border}`, p: 2.5, borderRadius: 1 }}>
            <Typography sx={{ color: '#888', fontSize: 11, letterSpacing: 1, fontFamily: 'monospace' }}>OVERALL STATUS</Typography>
            <Box sx={{ mt: 1 }}><StatusChip status={overall} /></Box>
            <Typography sx={{ color: '#666', fontSize: 11, mt: 1 }}>{data?.timestamp ? `Last checked ${data.timestamp}` : '—'}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><SummaryCard label="Healthy" value={data?.summary?.healthy} color="#66bb6a" icon={<MonitorHeartIcon fontSize="inherit" />} /></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><SummaryCard label="Warnings / Down" value={`${data?.summary?.warning ?? 0} / ${data?.summary?.down ?? 0}`} color="#ffb74d" icon={<StorageIcon fontSize="inherit" />} /></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><SummaryCard label="Slowest Critical Check" value={slowestCritical ? `${slowestCritical.latency_ms} ms` : '—'} color="#00bcd4" icon={<SpeedIcon fontSize="inherit" />} /></Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { color: '#777', fontSize: 11, fontFamily: 'monospace', borderColor: '#1e1e1e' } }}>
              <TableCell>COMPONENT</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell align="right">LATENCY</TableCell>
              <TableCell>DETAILS</TableCell>
              <TableCell>LAST CHECKED</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.checks || []).map((check) => (
              <TableRow key={check.component} sx={{ '& td': { borderColor: '#1e1e1e' }, '&:hover': { bgcolor: '#141414' } }}>
                <TableCell sx={{ color: '#e0e0e0', fontFamily: 'monospace', fontSize: 13 }}>
                  {check.component}
                  {!check.critical && <Typography component="span" sx={{ color: '#555', fontSize: 11, ml: 1 }}>(optional)</Typography>}
                </TableCell>
                <TableCell><StatusChip status={check.status} /></TableCell>
                <TableCell align="right" sx={{ color: check.latency_ms > 1000 ? '#ffb74d' : '#aaa', fontFamily: 'monospace' }}>{check.latency_ms} ms</TableCell>
                <TableCell sx={{ color: '#777', maxWidth: 520 }}>
                  <Tooltip title={formatDetails(check.details)} placement="top-start">
                    <Typography noWrap sx={{ fontSize: 12 }}>{formatDetails(check.details)}</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ color: '#555', fontSize: 12, fontFamily: 'monospace' }}>{check.checked_at}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </>}
    </Box>
  );
};

export default BuiltInMonitoring;
